import { useEffect } from 'react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { monthRange, shiftMonthParam } from '@/lib/calendar-month'

/**
 * The property calendar read, one query per visible month
 * (`GET /admin/properties/:id/calendar`, admin-api CAP-872).
 *
 * The key leads with `['property-calendar', propertyId]` so anything that
 * changes what the calendar shows can invalidate every month of one property
 * with that prefix - the calendar must only ever show what the API returned,
 * never an optimistic edit.
 */
export function propertyCalendarKey(propertyId: string | null | undefined) {
  return ['property-calendar', propertyId] as const
}

function monthQuery(propertyId: string, month: string) {
  const { from, to } = monthRange(month)
  return {
    queryKey: [...propertyCalendarKey(propertyId), from, to] as const,
    queryFn: () => api.getPropertyCalendar(propertyId, { from, to }),
    staleTime: 30_000,
  }
}

export function usePropertyCalendar(
  propertyId: string | null | undefined,
  month: string,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const queryClient = useQueryClient()
  const active = !!propertyId && enabled

  const query = useQuery({
    ...monthQuery(propertyId ?? '', month),
    enabled: active,
    // Paging months keeps the last month on screen (dimmed by the caller via
    // `isPlaceholderData`) instead of tearing the grid down for a skeleton on
    // the one interaction the tab exists for.
    placeholderData: keepPreviousData,
  })

  // The next month is what a click on "Next" asks for, so it is fetched while
  // the operator is still reading this one. Best-effort: a failed prefetch is
  // simply retried when the month is actually opened.
  useEffect(() => {
    if (!active) return
    void queryClient.prefetchQuery(monthQuery(propertyId, shiftMonthParam(month, 1)))
  }, [active, propertyId, month, queryClient])

  return query
}
